import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthModalProvider } from "@/context/AuthModalContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import MyTheatre from "@/pages/MyTheatre";
import BrowseShows from "@/pages/BrowseShows";
import Casts from "@/pages/Casts";
import ShowDetail from "@/pages/ShowDetail";
import ActorDetail from "@/pages/ActorDetail";
import News from "@/pages/News";
import Admin from "@/pages/Admin";
import AdminTrending from "@/pages/AdminTrending";
import About from "@/pages/About";
import Theatres from "@/pages/Theatres";
import TheatreGuide from "@/pages/TheatreGuide";
import TheatrePage from "@/pages/TheatrePage";
import NotFound from "@/pages/NotFound";
import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import { Loader2 } from "lucide-react";
import Privacy from "@/pages/Privacy";
import Quiz from "@/pages/Quiz";
import Recommend from "@/pages/Recommend";
import Trivia from "@/pages/Trivia";
import Unsubscribe from "@/pages/Unsubscribe";
import ShowPrices from "@/pages/ShowPrices";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicDiary from "@/pages/PublicDiary";
import InvitePage from "@/pages/InvitePage";
import ListPage from "@/pages/ListPage";
import AdminReports from "@/pages/AdminReports";

function Shell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null)
    return <div className="flex min-h-screen items-center justify-center bg-[#0D0A0E]"><Loader2 className="h-6 w-6 animate-spin text-[#C89B3C]" /></div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return <Shell>{children}</Shell>;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Auth />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <AuthModalProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/" element={<Shell><Home /></Shell>} />
          <Route path="/watchlist" element={<Shell><MyTheatre /></Shell>} />
          <Route path="/shows" element={<Shell><BrowseShows /></Shell>} />
          <Route path="/shows/:id" element={<Shell><ShowDetail /></Shell>} />
          <Route path="/shows/:id/prices" element={<Shell><ShowPrices /></Shell>} />
          <Route path="/casts" element={<Shell><Casts /></Shell>} />
          <Route path="/actors/:id" element={<Shell><ActorDetail /></Shell>} />
          <Route path="/news" element={<Shell><News /></Shell>} />
          <Route path="/about" element={<Shell><About /></Shell>} />
          <Route path="/theatres" element={<Shell><Theatres /></Shell>} />
          <Route path="/theatre-guide" element={<Shell><TheatreGuide /></Shell>} />
          <Route path="/theatres/:slug" element={<Shell><TheatrePage /></Shell>} />
          <Route path="/contact" element={<Shell><Contact /></Shell>} />
          <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
          <Route path="/admin/trending" element={<Protected adminOnly><AdminTrending /></Protected>} />
          <Route path="/privacy" element={<Shell><Privacy /></Shell>} />
          <Route path="/tiktok" element={<Navigate to="/?utm_source=tiktok&utm_medium=social&utm_campaign=bio" replace />} />
          <Route path="/unsubscribe" element={<Shell><Unsubscribe /></Shell>} />
          <Route path="/forgot-password" element={<Shell><ForgotPassword /></Shell>} />
          <Route path="/reset-password" element={<Shell><ResetPassword /></Shell>} />
          <Route path="/quiz" element={<Shell><Quiz /></Shell>} />
          <Route path="/find-a-show" element={<Shell><Recommend /></Shell>} />
          <Route path="/trivia" element={<Shell><Trivia /></Shell>} />
          <Route path="/u/:username" element={<Shell><PublicDiary /></Shell>} />
          <Route path="/list/:id" element={<Shell><ListPage /></Shell>} />
          <Route path="/invite/:code" element={<Shell><InvitePage /></Shell>} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="*" element={<Shell><NotFound /></Shell>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
      </AuthModalProvider>
    </AuthProvider>
  );
}

export default App;
